import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { groupAnalyses } from "../../lib/captureViews";
import { toast } from "sonner";
import { Radar, RadarChart, PolarAngleAxis, PolarGrid, PolarRadiusAxis, ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip } from "recharts";
import ReactMarkdown from "../../components/Markdown";
import { Sparkles, Pencil, Mail, Copy, Camera, Wand2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../../components/ui/dialog";
import VideoLibraryCard from "../../components/VideoLibraryCard";

const SKILLS = ["forehand", "backhand", "serve", "dink", "volley", "strategy"];

export default function CoachStudentDetail() {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [progress, setProgress] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [ratings, setRatings] = useState({ forehand: 3, backhand: 3, serve: 3, dink: 3, volley: 3, strategy: 3 });
  const [improve, setImprove] = useState("");
  const [notes, setNotes] = useState("");
  const [milestone, setMilestone] = useState("");
  const [saving, setSaving] = useState(false);
  const [summary, setSummary] = useState("");
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", email: "", phone: "", skill_level: 3.0, goals: "", bio: "" });
  const [editSaving, setEditSaving] = useState(false);
  const [resendOpen, setResendOpen] = useState(false);
  const [resendResult, setResendResult] = useState(null);
  const [resending, setResending] = useState(false);
  // "Pull from latest capture" prefill state. Surfaces which skills came
  // from the capture (highlighted) and which were left untouched.
  const [pulling, setPulling] = useState(false);
  const [pullEvidence, setPullEvidence] = useState(null); // { ratings, evidence, session }
  // This student's motion-capture sessions. `id` IS the student's user_id (the
  // roster links to /coach/students/{user_id}), which is exactly the value the
  // capture list filters on and the value tying a capture sets.
  const [captures, setCaptures] = useState([]);

  const load = async () => {
    try {
      const [s, p, a] = await Promise.all([
        api.get(`/students/${id}`),
        api.get(`/progress/student/${id}`),
        api.get(`/analytics/student/${id}`),
      ]);
      setStudent(s.data);
      setProgress(p.data || []);
      setAnalytics(a.data || null);
    } catch (_) {}
    // Separate try: a capture failure must not blank out the profile.
    try {
      const { data } = await api.get(`/capture/sessions`, { params: { student_id: id } });
      setCaptures(Array.isArray(data) ? data : []);
    } catch (_) {
      setCaptures([]);
    }
  };
  useEffect(() => { load(); }, [id]);

  const radarData = useMemo(() => {
    const latest = analytics?.latest_ratings || {};
    return SKILLS.map((k) => ({ skill: k.toUpperCase(), v: Number(latest[k] || 0) }));
  }, [analytics]);

  const trend = analytics?.trend || [];

  // One row per analysis. A two-camera capture is two sessions sharing a
  // `session_group_id`; listing both makes a single analysis look like two, and
  // inflates the count on the athlete's page. Keep the primary view and show the
  // angle count on the row — the detail page has the switcher.
  const analyses = useMemo(() => groupAnalyses(captures), [captures]);

  const saveProgress = async () => {
    setSaving(true);
    try {
      await api.post("/progress", {
        student_id: id,
        skill_ratings: ratings,
        areas_to_improve: improve,
        notes, milestone,
      });
      toast.success("Progress saved");
      setImprove(""); setNotes(""); setMilestone("");
      setPullEvidence(null);
      load();
    } catch (e) {
      toast.error("Could not save");
    }
    setSaving(false);
  };

  // Pre-fill the 1-5 sliders from the latest singles motion-capture session
  // for this student. Only overwrites skills that the clip actually surfaced
  // — `backhand` is always preserved (capture can't reliably distinguish).
  const pullFromCapture = async () => {
    setPulling(true);
    try {
      const { data } = await api.get(`/capture/latest-suggestion/${id}`);
      const suggested = data.ratings || {};
      const count = Object.keys(suggested).length;
      if (count === 0) {
        toast.message(data.message || "No capture data to pull from yet.");
        setPullEvidence(null);
      } else {
        setRatings((cur) => ({ ...cur, ...suggested }));
        setPullEvidence(data);
        toast.success(`Pre-filled ${count} skill${count === 1 ? "" : "s"} from your latest capture`);
      }
    } catch (e) {
      toast.error("Couldn't pull capture data");
    }
    setPulling(false);
  };

  const genSummary = async () => {
    setSummaryLoading(true);
    try {
      const { data } = await api.get(`/ai/progress-summary/${id}`);
      setSummary(data.summary);
    } catch (_) {
      toast.error("AI summary failed");
    }
    setSummaryLoading(false);
  };

  const openEdit = () => {
    setEditForm({
      name: student.name || "",
      email: student.email || "",
      phone: student.phone || "",
      skill_level: student.skill_level ?? 3.0,
      goals: student.goals || "",
      bio: student.bio || "",
    });
    setEditOpen(true);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    setEditSaving(true);
    try {
      const payload = {
        name: editForm.name,
        email: editForm.email,
        phone: editForm.phone,
        skill_level: parseFloat(editForm.skill_level) || 3.0,
        goals: editForm.goals,
        bio: editForm.bio,
      };
      await api.patch(`/students/${id}`, payload);
      toast.success("Student updated");
      setEditOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    }
    setEditSaving(false);
  };

  const resendInvite = async () => {
    if (!window.confirm(`Resend invite to ${student.name}? This will generate a new temporary password and email them.`)) return;
    setResending(true);
    try {
      const { data } = await api.post(`/students/${id}/resend-invite`);
      setResendResult(data);
      setResendOpen(true);
      toast.success("Invite resent");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not resend");
    }
    setResending(false);
  };

  const copyText = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success("Copied"); } catch (_) {}
  };

  if (!student) return <div className="text-[#A3A3A3]">Loading student…</div>;

  return (
    <div className="space-y-10" data-testid="student-detail-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <Link to="/coach/students" className="text-xs uppercase tracking-widest text-[#A3A3A3] hover:text-white" data-testid="back-students-link">← All students</Link>
          <h1 className="font-display text-5xl font-black uppercase tracking-tighter mt-2">{student.name}</h1>
          <div className="text-sm text-[#A3A3A3] mt-1">
            {student.email} · DUPR {student.skill_level}
            {student.phone ? ` · ${student.phone}` : ""}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Link
            to={`/coach/capture/new?student=${student.user_id}`}
            className="btn-primary px-5 py-3 text-xs inline-flex items-center gap-2"
            data-testid="new-capture-button"
          >
            <Camera size={14} /> New motion capture
          </Link>
          <button onClick={resendInvite} disabled={resending} className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="resend-invite-button">
            <Mail size={14} /> {resending ? "Sending…" : "Resend invite"}
          </button>
          <button onClick={openEdit} className="btn-secondary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="edit-student-button">
            <Pencil size={14} /> Edit profile
          </button>
        </div>
      </div>

      {/* Profile + goals */}
      <div className="grid md:grid-cols-3 gap-4">
        <div className="card-surface p-6 md:col-span-2">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Goals</div>
          <p className="text-base mt-3 text-white/90 whitespace-pre-wrap">{student.goals || "No goals set."}</p>
        </div>
        <div className="card-surface p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Stats</div>
          <div className="mt-4 space-y-2 text-sm">
            <Row k="Completed" v={analytics?.lessons_completed ?? 0} />
            <Row k="Upcoming" v={analytics?.lessons_upcoming ?? 0} />
            <Row k="Milestones" v={(analytics?.milestones || []).length} />
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-surface p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-4">Skill Radar</div>
          <div className="h-72">
            <ResponsiveContainer>
              <RadarChart data={radarData}>
                <PolarGrid stroke="rgba(255,255,255,0.1)" />
                <PolarAngleAxis dataKey="skill" stroke="#A3A3A3" tick={{ fontSize: 10 }} />
                <PolarRadiusAxis stroke="#A3A3A3" domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Radar dataKey="v" stroke="#38BDF8" fill="#38BDF8" fillOpacity={0.35} />
              </RadarChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="card-surface p-6">
          <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold mb-4">Skill Trend</div>
          <div className="h-72">
            <ResponsiveContainer>
              <LineChart data={trend}>
                <XAxis dataKey="date" stroke="#A3A3A3" tick={{ fontSize: 10 }} />
                <YAxis stroke="#A3A3A3" domain={[0, 5]} tick={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#141414", border: "1px solid rgba(255,255,255,0.1)" }} />
                {SKILLS.map((s, i) => (
                  <Line
                    key={s} dataKey={s} type="monotone"
                    stroke={i === 0 ? "#38BDF8" : `hsl(${(i * 45) % 360}, 60%, 65%)`}
                    dot={false} strokeWidth={2}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Log new progress */}
      <div className="card-surface p-8">
        <div className="flex items-start justify-between flex-wrap gap-3">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Log Progress</div>
            <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">After-lesson snapshot</h3>
          </div>
          <button
            onClick={pullFromCapture}
            disabled={pulling}
            className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2"
            data-testid="pull-from-capture-btn"
            title="Pre-fill the sliders from this student's most recent motion-capture session"
          >
            <Wand2 size={13} className="text-[#38BDF8]" />
            {pulling ? "Pulling…" : "Pull from latest capture"}
          </button>
        </div>

        <div className="mt-4 border-l-2 border-[#38BDF8] pl-4 py-1 text-xs text-[#A3A3A3]" data-testid="progress-rating-tip">
          Rate each skill on a <strong className="text-white">1–5 scale</strong> (1 = Learning, 3 = Reliable, 5 = Mastered).
          Only adjust skills you <strong className="text-white">actually observed this session</strong>. Not sure?{" "}
          <Link to="/coach/help" className="text-[#38BDF8] hover:underline font-bold">Read the rating guide →</Link>
        </div>

        {pullEvidence?.session && (
          <div
            className="mt-3 bg-[#38BDF8]/10 border border-[#38BDF8]/40 px-4 py-3 text-xs flex items-start gap-2"
            data-testid="pull-evidence-banner"
          >
            <Wand2 size={13} className="text-[#38BDF8] mt-0.5 flex-shrink-0" />
            <div className="space-y-1 leading-relaxed">
              <div>
                <span className="font-bold text-white">Pre-filled from capture</span>{" "}
                <span className="text-[#A3A3A3]">
                  · {new Date(pullEvidence.session.created_at).toLocaleDateString()}
                  {pullEvidence.session.rep_count ? ` · ${pullEvidence.session.rep_count} reps` : ""}
                  {pullEvidence.session.notes ? ` · ${pullEvidence.session.notes}` : ""}
                </span>
              </div>
              <div className="text-[#A3A3A3]">
                Pre-filled: <span className="text-[#38BDF8] font-bold">{Object.keys(pullEvidence.ratings).join(", ") || "—"}</span>
                {Object.keys(pullEvidence.ratings).length < SKILLS.length && (
                  <span>
                    {" "}· Untouched (no clip signal): <span className="text-white/70">
                      {SKILLS.filter((s) => !(s in pullEvidence.ratings)).join(", ")}
                    </span>
                  </span>
                )}
              </div>
              <Link
                to={`/coach/capture/${pullEvidence.session.id}`}
                className="text-[#38BDF8] hover:underline font-bold"
                data-testid="pull-evidence-link"
              >
                Open the source capture →
              </Link>
            </div>
          </div>
        )}

        <div className="grid md:grid-cols-3 gap-6 mt-6">
          {SKILLS.map((k) => {
            const fromCapture = pullEvidence?.ratings && k in pullEvidence.ratings;
            return (
              <div key={k}>
                <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold capitalize flex items-center gap-1.5">
                  <span>{k} ({ratings[k]})</span>
                  {fromCapture && (
                    <span
                      className="text-[9px] text-[#38BDF8] border border-[#38BDF8]/40 px-1 py-0.5"
                      data-testid={`rating-from-capture-${k}`}
                      title="Suggested by motion capture — edit before saving"
                    >FROM CAPTURE</span>
                  )}
                </label>
                <input
                  type="range" min={1} max={5} step={0.5}
                  value={ratings[k]}
                  onChange={(e) => setRatings({ ...ratings, [k]: Number(e.target.value) })}
                  className="w-full accent-[#38BDF8]"
                  data-testid={`rating-${k}`}
                />
              </div>
            );
          })}
        </div>

        <div className="grid md:grid-cols-3 gap-4 mt-6">
          <TextArea label="Areas to improve" value={improve} onChange={setImprove} testId="areas-input" />
          <TextArea label="Coach notes" value={notes} onChange={setNotes} testId="notes-input" />
          <TextArea label="Milestone (optional)" value={milestone} onChange={setMilestone} testId="milestone-input" placeholder="e.g. First clean ATP" />
        </div>

        <button onClick={saveProgress} disabled={saving} className="btn-primary mt-6 px-6 py-3 text-xs" data-testid="save-progress-button">
          {saving ? "Saving…" : "Save progress"}
        </button>
      </div>

      {/* Motion captures tied to this athlete. Anything captured with this
          student selected (or tied to them afterwards) shows up here — a
          capture tied to a student must be reachable FROM that student. */}
      <div className="card-surface p-8" data-testid="student-captures-card">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">Motion capture</div>
            <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">
              Video analyses ({analyses.length})
            </h3>
          </div>
          <Link
            to={`/coach/capture/new?student=${id}`}
            className="btn-secondary px-3 py-2 text-xs inline-flex items-center gap-2"
            data-testid="student-capture-new"
          >
            <Camera size={13} /> New analysis
          </Link>
        </div>

        {analyses.length === 0 ? (
          <div className="mt-6 text-sm text-[#A3A3A3]" data-testid="student-captures-empty">
            No analyses tied to {student?.name?.split(" ")[0] || "this student"} yet. Record one above, or open an
            existing session and set the athlete on it.
          </div>
        ) : (
          <div className="mt-6 space-y-3" data-testid="student-captures-list">
            {analyses.map((c) => (
              <Link
                key={c.id}
                to={`/coach/capture/${c.id}`}
                className="flex items-center justify-between gap-4 border border-white/5 hover:border-[#38BDF8]/50 px-4 py-3 transition-colors"
                data-testid={`student-capture-${c.id}`}
              >
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
                    {c.created_at ? new Date(c.created_at).toLocaleString() : ""}
                    <span className="text-[#38BDF8] ml-2">{c.play_mode || "drill"}</span>
                    {(c.group_size || 1) > 1 && (
                      <span className="text-[#38BDF8] ml-2" data-testid={`student-capture-angles-${c.id}`}>
                        · {c.group_size} angles
                      </span>
                    )}
                    {c.is_public && <span className="text-[#00ff88] ml-2">· shared</span>}
                  </div>
                  <div className="text-sm text-white/85 mt-1 truncate">
                    {c.ai_preview || c.notes || "Analysis ready to view"}
                  </div>
                </div>
                <span className="text-[10px] uppercase tracking-widest text-[#38BDF8] font-bold flex-shrink-0">
                  Open →
                </span>
              </Link>
            ))}
          </div>
        )}
      </div>

      <VideoLibraryCard studentId={id} role="coach" />

      {/* Progress history timeline */}
      <div className="card-surface p-8" data-testid="progress-history-card">
        <div className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">History</div>
            <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">Progress log ({progress.length})</h3>
          </div>
        </div>
        {progress.length === 0 ? (
          <div className="mt-6 text-sm text-[#A3A3A3]">No entries yet. Log your first progress snapshot above.</div>
        ) : (
          <div className="mt-6 space-y-5" data-testid="progress-history-list">
            {progress.slice().reverse().map((p) => (
              <div key={p.id} className="border-l-2 border-[#38BDF8] pl-4 py-1" data-testid={`progress-${p.id}`}>
                <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">
                  {new Date(p.created_at).toLocaleString()}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {SKILLS.map((s) => (
                    <span key={s} className="text-[10px] uppercase tracking-widest border border-white/10 px-2 py-1">
                      {s} <span className="text-[#38BDF8] font-bold ml-1">{Number((p.skill_ratings || {})[s] || 0)}</span>
                    </span>
                  ))}
                </div>
                {p.milestone && (
                  <div className="mt-2 text-sm text-[#38BDF8] font-bold">🏆 {p.milestone}</div>
                )}
                {p.areas_to_improve && (
                  <div className="mt-2 text-xs"><span className="text-[#A3A3A3] uppercase tracking-widest">Improve:</span> {p.areas_to_improve}</div>
                )}
                {p.notes && (
                  <div className="mt-1 text-xs text-white/80 whitespace-pre-wrap"><span className="text-[#A3A3A3] uppercase tracking-widest">Notes:</span> {p.notes}</div>
                )}
                {p.insights && (
                  <div className="mt-2 text-xs text-[#38BDF8] whitespace-pre-wrap" data-testid={`progress-insight-${p.id}`}>
                    <span className="text-[#A3A3A3] uppercase tracking-widest">Insight:</span> {p.insights}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* AI Summary */}
      <div className="card-surface p-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <div className="text-[10px] tracking-[0.3em] uppercase text-[#38BDF8] font-bold">AI Coach Report</div>
            <h3 className="font-display text-3xl font-black uppercase tracking-tighter mt-2">Progress summary</h3>
          </div>
          <button onClick={genSummary} disabled={summaryLoading} className="btn-primary px-5 py-3 text-xs inline-flex items-center gap-2" data-testid="generate-summary-button">
            <Sparkles size={14} /> {summaryLoading ? "Generating…" : "Generate report"}
          </button>
        </div>
        {summary && (
          <div className="mt-6 prose prose-invert max-w-none text-sm" data-testid="ai-summary-output">
            <ReactMarkdown>{summary}</ReactMarkdown>
          </div>
        )}
      </div>

      {/* Edit student dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-lg" data-testid="edit-student-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Edit student</DialogTitle>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4">
            <EditField label="Full name" value={editForm.name} onChange={(v) => setEditForm({ ...editForm, name: v })} required testId="edit-student-name" />
            <EditField label="Email" type="email" value={editForm.email} onChange={(v) => setEditForm({ ...editForm, email: v })} required testId="edit-student-email" />
            <EditField label="Phone" type="tel" value={editForm.phone} onChange={(v) => setEditForm({ ...editForm, phone: v })} testId="edit-student-phone" />
            <EditField label="Skill level (DUPR)" type="number" step="0.1" min="1.0" max="6.0"
                       value={editForm.skill_level} onChange={(v) => setEditForm({ ...editForm, skill_level: v })} testId="edit-student-skill" />
            <div>
              <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Goals</label>
              <textarea rows={2} value={editForm.goals} onChange={(e) => setEditForm({ ...editForm, goals: e.target.value })}
                className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-1"
                data-testid="edit-student-goals" />
            </div>
            <div>
              <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">Bio (optional)</label>
              <textarea rows={2} value={editForm.bio} onChange={(e) => setEditForm({ ...editForm, bio: e.target.value })}
                className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-1"
                data-testid="edit-student-bio" />
            </div>
            <button disabled={editSaving} className="btn-primary w-full py-3 text-sm" data-testid="edit-student-submit">
              {editSaving ? "Saving…" : "Save changes"}
            </button>
          </form>
        </DialogContent>
      </Dialog>
      {/* Resend invite result dialog */}
      <Dialog open={resendOpen} onOpenChange={setResendOpen}>
        <DialogContent className="rounded-none bg-[#141414] border-white/10 max-w-md" data-testid="resend-invite-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">Invite resent</DialogTitle>
          </DialogHeader>
          {resendResult && (
            <div className="space-y-4">
              <p className="text-sm text-white/90">
                A fresh invite email has been sent to <strong>{resendResult.email}</strong>. Share these credentials directly too:
              </p>
              <div className="flex items-center justify-between border border-white/10 px-3 py-2 bg-white/[0.02]">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">Email</div>
                  <div className="font-mono text-sm mt-0.5">{resendResult.email}</div>
                </div>
                <button onClick={() => copyText(resendResult.email)} className="p-2 hover:bg-white/5"><Copy size={14} /></button>
              </div>
              <div className="flex items-center justify-between border border-white/10 px-3 py-2 bg-white/[0.02]">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-[#A3A3A3] font-bold">New temporary password</div>
                  <div className="font-mono text-sm mt-0.5">{resendResult.temp_password}</div>
                </div>
                <button onClick={() => copyText(resendResult.temp_password)} className="p-2 hover:bg-white/5"><Copy size={14} /></button>
              </div>
              <p className="text-xs text-[#A3A3A3]">This password replaces the previous one — any old password no longer works.</p>
              <button onClick={() => setResendOpen(false)} className="btn-primary w-full py-3 text-sm" data-testid="resend-close-button">Done</button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function EditField({ label, type = "text", value, onChange, required, testId, step, min, max }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <input
        type={type} value={value} onChange={(e) => onChange(e.target.value)} required={required}
        step={step} min={min} max={max}
        className="w-full bg-transparent border-0 border-b border-white/20 focus:border-[#38BDF8] outline-none py-2 text-sm"
        data-testid={testId}
      />
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-center justify-between border-b border-white/5 py-2">
      <span className="text-[#A3A3A3]">{k}</span>
      <span className="font-display font-bold text-xl">{v}</span>
    </div>
  );
}

function TextArea({ label, value, onChange, testId, placeholder }) {
  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-bold">{label}</label>
      <textarea
        rows={3}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full bg-transparent border border-white/10 focus:border-[#38BDF8] outline-none p-3 text-sm mt-2"
        data-testid={testId}
      />
    </div>
  );
}
